import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Trash2 } from 'lucide-react'
import {
  PROJECT_BILLING_SCHEMES,
  PROJECT_INITIAL_STATUSES,
  PROJECT_NOTE_COLORS,
  billingIntervalAllowed,
  billingIntervalsFor,
  erfuelltRecht,
  workOrderDefaults,
} from '@symphenia/shared-types'
import { cardTitle, formCardSm, inlineInput, pageTitle, pageWide } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { useTerminology } from '@/lib/useTerminology'
import { HelpPopover } from '@/components/ui/HelpPopover'
import {
  FeldStatus,
  FeldStatusZeichen,
  Feldgruppe,
  feldKlasse,
  useAenderung,
  useFeld,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { TimeBridgeCard } from '../components/TimeBridgeCard'
import { TechSettingsTab } from '../components/TechSettingsTab'
import { BomSettingsTab } from '../components/BomSettingsTab'
import { AuftragAusdruckKarte } from '../components/AuftragAusdruckKarte'
import { NeueRegieSatzZeile, RegieSatzZeile, abrechnungsFehlerText } from '../components/ProjectBillingTab'
import { fetchUnits } from '../api/project-bom.api'
import {
  createJournalType,
  createPhase,
  createQualification,
  createTenantRegieRate,
  deletePhase,
  deleteQualification,
  deleteTenantRegieRate,
  fetchJournalTypes,
  fetchPhases,
  fetchProjectSettings,
  fetchQualifications,
  fetchTenantRegieRates,
  fetchUsers,
  updateTenantRegieRate,
} from '../api/projects-head.api'
import { useProjektEinstellungen, useZeilenStelle } from '../lib/projekt-einstellungen'
import { BILLING_INTERVAL_LABELS, BILLING_SCHEME_LABELS, NOTE_COLOR_LABELS, PROJECT_STATUS_LABELS } from '../project-labels'
import type { JournalType, ProjectPhase, ProjectQualification, ProjectRegieRate, ProjectSettings } from '../types/project.types'
import { bestaetige } from '@/lib/dialoge'

/**
 * AP-35 Detailblatt 13 — **Einstellungen des Projektmanagements.**
 *
 * Ein Ort, Reiter je Bereich. **Welche Reiter es gibt, sagt der Server**
 * (`tabs`): Was zu einem nicht gebuchten Baustein gehört, fehlt in der Antwort
 * und damit hier — die Maske rät nicht (AP-29-N3). Geschrieben wird je Reiter,
 * nie die ganze Zeile.
 *
 * AP-36 Phase D: ohne Knopf. Alle Felder der Einstellungszeile hängen an einer
 * Stelle (`projectSettings`, `lib/projekt-einstellungen.ts`), die den Patch dem
 * Reiter des Felds zuordnet. Nummernkreis und Ampel-Schwellen sind je eine
 * Gruppe — der Server prüft sie am zusammengeführten Stand. Die Listen (Phasen,
 * Qualifikationen, Journal-Typen) speichern je Zeile.
 */
const REITER: { key: string; label: string }[] = [
  { key: 'allgemein', label: 'Allgemein' },
  { key: 'nummernkreis', label: 'Nummernkreis' },
  { key: 'phasen', label: 'Phasen' },
  { key: 'qualifikationen', label: 'Qualifikationen & Regiesätze' },
  { key: 'journal', label: 'Journal' },
  { key: 'zeiterfassung', label: 'Zeiterfassung' },
  { key: 'technik', label: 'Technik & CE' },
  { key: 'stueckliste', label: 'Stückliste & Einkauf' },
  { key: 'abrechnung', label: 'Abrechnung' },
  { key: 'ampeln', label: 'Ampeln & Cockpits' },
  { key: 'pwa', label: 'Mitarbeiter-App' },
]

export default function ProjectSettingsPage() {
  const terms = useTerminology()
  const { user, hasModule } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfAendern = darf('projects.settings.manage')

  const [settings, setSettings] = useState<ProjectSettings | null>(null)
  const [benutzer, setBenutzer] = useState<{ wert: string; text: string }[]>([])
  const [einheiten, setEinheiten] = useState<{ wert: string; text: string }[]>([])
  const [reiter, setReiter] = useState('allgemein')
  const [fehler, setFehler] = useState<string | null>(null)

  const stelle = useProjektEinstellungen(settings as unknown as Datensatz | null)
  const werte = useStellenDaten(stelle) as unknown as ProjectSettings

  useEffect(() => {
    void fetchProjectSettings()
      .then(setSettings)
      .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
    void fetchUsers()
      .then((u) => setBenutzer(u.map((b) => ({ wert: b.id, text: b.name }))))
      .catch(() => undefined)
    void fetchUnits()
      .then((u) => setEinheiten(u.map((e) => ({ wert: e.id, text: `${e.code} · ${e.name}` }))))
      .catch(() => undefined)
  }, [])

  if (!settings) return <div className="p-6 text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</div>

  // Reiter, die kein Feld der Einstellungszeile tragen (Listen), sind immer da;
  // die übrigen nur, wenn ihr Baustein gebucht ist.
  const listenReiter = new Set(['phasen', 'qualifikationen', 'journal'])
  const sichtbar = REITER.filter((r) => listenReiter.has(r.key) || settings.tabs.includes(r.key))
  const karte = { stelle, darfAendern }

  return (
    <div className={pageWide}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className={pageTitle}>Einstellungen · {terms.management}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vorgaben, Nummernkreis, Phasen, Qualifikationen und Schwellen. Was zu einem nicht gebuchten Baustein
            gehört, erscheint hier nicht. Jede Änderung speichert beim Verlassen des Felds.
          </p>
        </div>
        <HelpPopover topic="columns" />
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {sichtbar.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setReiter(r.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${
              reiter === r.key
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}

      {reiter === 'allgemein' && (
        <FelderKarte
          {...karte}
          titel="Vorgaben"
          felder={[
            { feld: 'defaultStatus', label: 'Anfangsstatus', art: 'auswahl', optionen: PROJECT_INITIAL_STATUSES.map((s) => ({ wert: s, text: PROJECT_STATUS_LABELS[s].label })) },
            { feld: 'requireClientOnOrder', label: 'Kunde Pflicht', art: 'schalter' },
            { feld: 'requireSite', label: 'Betriebsstätte Pflicht', art: 'schalter' },
            { feld: 'warrantyMonths', label: 'Gewährleistung (Monate)', art: 'zahl' },
            { feld: 'idleDays', label: 'ohne Bewegung ab (Tagen)', art: 'zahl' },
            { feld: 'maxFileMb', label: 'Dateigröße je Anhang (MB)', art: 'zahl' },
            { feld: 'defaultUnitId', label: 'Einheit der ersten Position', art: 'auswahl', leerErlaubt: true, optionen: [{ wert: '', text: '— PA —' }, ...einheiten] },
            { feld: 'defaultSalesUserId', label: 'Verkauf (Vorgabe)', art: 'auswahl', leerErlaubt: true, optionen: [{ wert: '', text: '— niemand —' }, ...benutzer] },
            { feld: 'defaultCalculationUserId', label: 'Kalkulation (Vorgabe)', art: 'auswahl', leerErlaubt: true, optionen: [{ wert: '', text: '— niemand —' }, ...benutzer] },
            {
              feld: 'workStepQuantityBasisDefault',
              label: 'Mengenbezug neuer Arbeitsschritte',
              art: 'auswahl',
              optionen: [
                { wert: 'TOTAL', text: 'gesamt' },
                { wert: 'PER_UNIT', text: 'je Einheit' },
              ],
            },
          ]}
        />
      )}

      {reiter === 'nummernkreis' && (
        <FelderKarte
          {...karte}
          // Muster und Jahresschnitt prüft der Server zusammen — eine Gruppe.
          gruppe
          titel={`Nummernkreis · nächste Nummer ${werte.nextNumberPreview ?? '—'}`}
          felder={[
            { feld: 'numberPattern', label: 'Muster', art: 'text', hinweis: 'Platzhalter: {prefix} {jahr} {jahr2} {monat} {nummer}' },
            { feld: 'numberPrefix', label: 'Präfix', art: 'text', leerErlaubt: true },
            { feld: 'numberDigits', label: 'Stellen', art: 'zahl' },
            { feld: 'yearlyReset', label: 'jährlich zurücksetzen', art: 'schalter' },
            { feld: 'allowManualNumber', label: 'Nummer von Hand erlauben', art: 'schalter' },
            { feld: 'nextWorkStepNumber', label: 'nächste Arbeitsschritt-Nummer', art: 'zahl' },
          ]}
        />
      )}

      {reiter === 'zeiterfassung' && (
        <FelderKarte
          {...karte}
          titel="Zeiterfassung"
          felder={[
            { feld: 'mirrorAll', label: 'alle Positionen spiegeln', art: 'schalter' },
            { feld: 'actualsCheckedOnly', label: 'Nachkalkulation nur geprüfte Buchungen', art: 'schalter' },
            { feld: 'defaultPlannedCostRate', label: 'Sollsatz-Vorgabe', art: 'zahl', leerErlaubt: true },
          ]}
        />
      )}
      {/* Die Brücke verlangt die Zeiterfassung (der Server antwortet sonst 403). */}
      {reiter === 'zeiterfassung' && hasModule('TIME_TRACKING.CORE') && <TimeBridgeCard />}

      {reiter === 'abrechnung' && <AbrechnungsVorgabeKarte {...karte} />}
      {reiter === 'abrechnung' && (
        <FelderKarte
          {...karte}
          titel="Abrechnung"
          felder={[
            { feld: 'billingWeekday', label: 'Stichtag (1 = Montag)', art: 'zahl' },
            {
              feld: 'regieGrouping',
              label: 'Regievorschlag gruppieren',
              art: 'auswahl',
              optionen: [
                { wert: 'QUALIFICATION_DAY', text: 'Qualifikation und Tag' },
                { wert: 'QUALIFICATION', text: 'Qualifikation' },
                { wert: 'USER_DAY', text: 'Mitarbeiter und Tag' },
                { wert: 'DAY', text: 'Tag' },
              ],
            },
            { feld: 'regieNamesOnInvoice', label: 'Namen auf dem Beleg', art: 'schalter' },
            { feld: 'invoiceRequiresDelivery', label: 'Rechnung nur aus Lieferschein', art: 'schalter' },
          ]}
        />
      )}

      {reiter === 'ampeln' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <FelderKarte
            {...karte}
            // Warnung und Alarm prüft der Server zusammen — eine Gruppe.
            gruppe
            titel="Ampeln"
            felder={[
              { feld: 'scheduleWarnDays', label: 'Termin-Puffer (Tage)', art: 'zahl' },
              { feld: 'progressWarnPoints', label: 'Leistungsstand Warnung (Punkte)', art: 'zahl' },
              { feld: 'progressAlertPoints', label: 'Leistungsstand Alarm (Punkte)', art: 'zahl' },
              { feld: 'costWarnFactor', label: 'Kosten Warnfaktor', art: 'zahl' },
              { feld: 'costAlertPercent', label: 'Kosten Alarm (%)', art: 'zahl' },
            ]}
          />
          <FelderKarte
            {...karte}
            titel="Cockpits"
            felder={[
              { feld: 'cockpitHideDoneAfterDays', label: 'fertige Karten ausblenden nach (Tagen)', art: 'zahl' },
              { feld: 'assignmentDefaultDays', label: 'Vorgabe-Zeitraum Einteilung (Tage)', art: 'zahl' },
              { feld: 'dayHoursWarn', label: 'Warnung Tagesstunden', art: 'zahl' },
              { feld: 'weekHoursWarn', label: 'Warnung Wochenstunden', art: 'zahl' },
            ]}
          />
        </div>
      )}

      {reiter === 'pwa' && (
        <FelderKarte {...karte} titel="Mitarbeiter-App" felder={[{ feld: 'folderBundleMaxPositions', label: 'Grenze Offline-Bundle (Positionen)', art: 'zahl' }]} />
      )}

      {reiter === 'technik' && <TechSettingsTab darfAendern={darfAendern} stelle={stelle} />}
      {reiter === 'stueckliste' && <BomSettingsTab darfAendern={darfAendern} stelle={stelle} />}
      {/* AP-35 Nachtrag 10: der Auftrag auf Briefpapier oder mit Logo — gespeichert mit den allgemeinen Vorgaben. */}
      {reiter === 'phasen' && <AuftragAusdruckKarte stelle={stelle} darfAendern={darfAendern} />}
      {reiter === 'phasen' && <PhasenKarte darfAendern={darfAendern} />}
      {reiter === 'qualifikationen' && <QualifikationenKarte darfAendern={darfAendern} />}
      {reiter === 'journal' && <JournalKarte {...karte} />}
    </div>
  )
}

// ── Vorgabe-Schema und -Intervall (AP-35 Nachtrag 18.9.2026) ───────────────

/**
 * Schema und Intervall hängen zusammen: Das Intervall bietet nur an, was zum
 * Schema passt (`billingIntervalsFor`, shared-types). Ein Schemawechsel nimmt
 * ein unpassendes Intervall als **Folgefeld** mit — auf das erste erlaubte.
 */
function AbrechnungsVorgabeKarte({ stelle, darfAendern }: { stelle: Speicherstelle<unknown>; darfAendern: boolean }) {
  const d = useStellenDaten(stelle)
  const intervallJetzt = String(d.defaultBillingInterval ?? '')
  const schema = useFeld(stelle, 'defaultBillingScheme', {
    art: 'auswahl',
    label: 'Vorgabe-Schema',
    pflicht: true,
    gesperrt: !darfAendern,
    folge: (neu) => (neu && !billingIntervalAllowed(neu, intervallJetzt) ? { defaultBillingInterval: billingIntervalsFor(neu)[0] ?? null } : {}),
  })
  const intervall = useFeld(stelle, 'defaultBillingInterval', {
    art: 'auswahl',
    label: 'Vorgabe-Intervall',
    pflicht: true,
    gesperrt: !darfAendern,
    pruefe: (w) => (w && !billingIntervalAllowed(String(schema.wert ?? ''), w) ? 'Passt nicht zum Schema.' : null),
  })

  // Ohne den Baustein `.BILLING` schickt der Server die Felder nicht.
  if (d.defaultBillingScheme === undefined) return null
  const intervalle = billingIntervalsFor(String(schema.wert ?? ''))
  const aktuell = String(intervall.wert ?? '')

  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>Abrechnungsmodalitäten (Vorgabe)</h3>
      <div className="mt-3 space-y-2">
        <Zeile label="Vorgabe-Schema" feld={schema}>
          <select className={`${feldKlasse} max-w-xs`} {...schema.props}>
            {PROJECT_BILLING_SCHEMES.map((s) => (
              <option key={s} value={s}>
                {BILLING_SCHEME_LABELS[s] ?? s}
              </option>
            ))}
          </select>
        </Zeile>
        <Zeile label="Vorgabe-Intervall" feld={intervall}>
          <select className={`${feldKlasse} max-w-xs`} {...intervall.props}>
            {aktuell && !(intervalle as readonly string[]).includes(aktuell) && (
              <option value={aktuell} disabled>
                {BILLING_INTERVAL_LABELS[aktuell] ?? aktuell} (passt nicht zum Schema)
              </option>
            )}
            {intervalle.map((i) => (
              <option key={i} value={i}>
                {BILLING_INTERVAL_LABELS[i] ?? i}
              </option>
            ))}
          </select>
        </Zeile>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Regie rechnet nach dem Kalender ab, Pauschal und Einheitspreis nach einem Ereignis — das Intervall bietet nur
        an, was zum Schema passt.
      </p>
    </div>
  )
}

// ── Eine Karte mit Beschriftung links, Wert rechts (Muster Blatt 1) ─────────

interface FeldDef {
  feld: string
  label: string
  art: 'text' | 'zahl' | 'schalter' | 'auswahl'
  optionen?: readonly { wert: string; text: string }[]
  hinweis?: string
  /** Darf leer sein (Spalte nullable). Sonst ist ein leeres Feld ein Versehen, kein Wert. */
  leerErlaubt?: boolean
}

/** Beschriftung links, Feld rechts, Zustand daneben, Fehler darunter. */
function Zeile({ label, feld, hinweis, children }: { label: string; feld: Parameters<typeof FeldStatus>[0]['status']; hinweis?: string; children: ReactNode }) {
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <label className="w-64 shrink-0 text-muted-foreground">{label}</label>
        {children}
        <FeldStatusZeichen status={feld} />
        {hinweis && <span className="text-xs text-muted-foreground">{hinweis}</span>}
      </div>
      <div className="sm:pl-[16.75rem]">
        <FeldStatus status={feld} zeichen={false} />
      </div>
    </div>
  )
}

function EinFeld({ stelle, def, darfAendern }: { stelle: Speicherstelle<unknown>; def: FeldDef; darfAendern: boolean }) {
  const f = useFeld<unknown>(stelle, def.feld, {
    art: def.art,
    label: def.label,
    gesperrt: !darfAendern,
    pflicht: def.art !== 'schalter' && !def.leerErlaubt,
  })
  if (def.art === 'schalter')
    return (
      <Zeile label={def.label} feld={f} hinweis={def.hinweis}>
        <input type="checkbox" {...f.schalter} />
      </Zeile>
    )
  return (
    <Zeile label={def.label} feld={f} hinweis={def.hinweis}>
      {def.art === 'auswahl' ? (
        <select className={`${feldKlasse} max-w-xs`} {...f.props}>
          {(def.optionen ?? []).map((o) => (
            <option key={o.wert} value={o.wert}>
              {o.text}
            </option>
          ))}
        </select>
      ) : (
        <input className={`${feldKlasse} max-w-xs`} inputMode={def.art === 'zahl' ? 'decimal' : 'text'} {...f.props} />
      )}
    </Zeile>
  )
}

function FelderKarte({
  titel,
  felder,
  stelle,
  darfAendern,
  gruppe,
}: {
  titel: string
  felder: readonly FeldDef[]
  stelle: Speicherstelle<unknown>
  darfAendern: boolean
  /** Die Felder sind nur zusammen gültig — speichern beim Verlassen der Karte. */
  gruppe?: boolean
}) {
  const werte = useStellenDaten(stelle)
  // Nur Felder, die der Server geschickt hat — der Rest gehört einem nicht
  // gebuchten Baustein und wird nicht angeboten.
  const vorhanden = felder.filter((f) => werte[f.feld] !== undefined)
  const inhalt = vorhanden.map((f) => <EinFeld key={f.feld} stelle={stelle} def={f} darfAendern={darfAendern} />)

  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>{titel}</h3>
      {gruppe ? (
        <Feldgruppe stelle={stelle} label={titel} className="mt-3 space-y-2">
          {inhalt}
        </Feldgruppe>
      ) : (
        <div className="mt-3 space-y-2">{inhalt}</div>
      )}
    </div>
  )
}

// ── Listen ─────────────────────────────────────────────────────────────────

/** Ein Textfeld einer Listenzeile. */
function ZellenFeld({ stelle, feld, label, darf, klasse = '', pflicht, zahl }: {
  stelle: Speicherstelle<unknown>
  feld: string
  label: string
  darf: boolean
  klasse?: string
  pflicht?: boolean
  zahl?: boolean
}) {
  const f = useFeld<unknown>(stelle, feld, { label, pflicht, gesperrt: !darf, art: zahl ? 'zahl' : 'text' })
  return (
    <>
      <span className="flex items-center gap-1">
        <input className={`${inlineInput} ${klasse}`} aria-label={label} inputMode={zahl ? 'decimal' : 'text'} {...f.props} />
        <FeldStatusZeichen status={f} />
      </span>
      <FeldStatus status={f} zeichen={false} />
    </>
  )
}

function ZellenHaken({ stelle, feld, label, darf }: { stelle: Speicherstelle<unknown>; feld: string; label: string; darf: boolean }) {
  const f = useFeld<boolean>(stelle, feld, { art: 'schalter', label, gesperrt: !darf })
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <input type="checkbox" aria-label={label} {...f.schalter} />
        <FeldStatusZeichen status={f} />
      </span>
      <FeldStatus status={f} zeichen={false} />
    </>
  )
}

const phasenStelle = (p: ProjectPhase) => ['projectPhase', `/projects/settings/phases/${p.id}`, p, p.name] as const

function PhasenKarte({ darfAendern }: { darfAendern: boolean }) {
  const [phasen, setPhasen] = useState<ProjectPhase[]>([])
  const [benutzer, setBenutzer] = useState<{ id: string; name: string }[]>([])
  const [neu, setNeu] = useState({ key: '', name: '' })
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(() => {
    void fetchPhases().then(setPhasen).catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
  }, [])
  useEffect(laden, [laden])
  useEffect(() => {
    void fetchUsers().then(setBenutzer).catch(() => undefined)
  }, [])

  return (
    <div className={formCardSm}>
      <h3 className={cardTitle}>Phasen</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Eine Liste für Termine, Zuständige, Arbeitsschritt-Kategorie, Arbeits-Cockpit und Nachkalkulation. Genau eine Phase ist die
        Montage.
      </p>
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      <table className="mt-3 w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pr-2">Schlüssel</th>
            <th className="py-1.5 pr-2">Name</th>
            <th className="py-1.5 pr-2">Reihenfolge</th>
            <th className="py-1.5 pr-2">Farbe</th>
            <th className="py-1.5 pr-2">Zuständig (Vorgabe)</th>
            <th className="py-1.5 pr-2">Montage</th>
            <th className="py-1.5 pr-2">zählt als Arbeit</th>
            <th className="py-1.5 pr-2">aktiv</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {phasen.map((p) => (
            <PhasenZeile key={p.id} phase={p} benutzer={benutzer} darf={darfAendern} onGeaendert={laden} onFehler={setFehler} />
          ))}
        </tbody>
      </table>

      {darfAendern && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input
            className={inlineInput}
            placeholder="Schlüssel (z. B. PAINTING)"
            value={neu.key}
            onChange={(e) => setNeu((n) => ({ ...n, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }))}
          />
          <input
            className={inlineInput}
            placeholder="Name"
            value={neu.name}
            onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))}
          />
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            disabled={!neu.key || !neu.name}
            onClick={async () => {
              try {
                await createPhase(neu)
                setNeu({ key: '', name: '' })
                laden()
              } catch (e) {
                setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
              }
            }}
          >
            Phase anlegen
          </button>
        </div>
      )}

      {/* AP-35 Nachtrag 9: Überschrift und Unterschriftzeilen des Auftrags je Kategorie. Leer = Vorgabe (grau). */}
      <h4 className="mt-5 text-sm font-medium">Auftrag je Kategorie</h4>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Überschrift und Unterschriftzeilen des gedruckten Auftrags. Leer lassen übernimmt die grau angezeigte Vorgabe; eine
        Unterschriftzeile je Zeile.
      </p>
      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pr-2">Kategorie</th>
            <th className="py-1.5 pr-2">Auftragsbezeichnung</th>
            <th className="py-1.5 pr-2">Unterschriftzeilen</th>
          </tr>
        </thead>
        <tbody>
          {phasen
            .filter((p) => p.isActive)
            .map((p) => (
              <AuftragJeKategorie key={p.id} phase={p} darf={darfAendern} />
            ))}
        </tbody>
      </table>
    </div>
  )
}

/** Eine Phase — eine Stelle (`projectPhase:<id>`), geteilt mit „Auftrag je Kategorie". */
function PhasenZeile({
  phase: p,
  benutzer,
  darf,
  onGeaendert,
  onFehler,
}: {
  phase: ProjectPhase
  benutzer: { id: string; name: string }[]
  darf: boolean
  onGeaendert: () => void
  onFehler: (text: string | null) => void
}) {
  const stelle = useZeilenStelle(...phasenStelle(p))
  const d = useStellenDaten(stelle) as unknown as ProjectPhase
  const farbe = useFeld(stelle, 'color', { art: 'auswahl', label: 'Farbe', gesperrt: !darf })
  const zustaendig = useFeld(stelle, 'defaultResponsibleUserId', { art: 'auswahl', label: 'Zuständig (Vorgabe)', gesperrt: !darf })
  // Montage ist genau eine Phase — der Server nimmt sie der bisherigen weg, die Liste lädt neu.
  const montage = useAenderung(stelle, `projectPhase:${p.id}:montage`)

  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2 font-mono text-xs">{p.key}</td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="name" label="Name" darf={darf} pflicht />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="sortOrder" label="Reihenfolge" darf={darf} klasse="w-16 text-right" pflicht zahl />
      </td>
      <td className="py-1.5 pr-2">
        <span className="inline-flex items-center gap-1">
          <FarbFeld wert={farbe.wert && /^#[0-9a-f]{6}$/i.test(farbe.wert) ? farbe.wert : '#94a3b8'} gesperrt={!darf} onWahl={(hex) => farbe.setze(hex)} />
          <FeldStatusZeichen status={farbe} />
        </span>
      </td>
      <td className="py-1.5 pr-2">
        <select className={inlineInput} aria-label="Zuständig (Vorgabe)" {...zustaendig.props}>
          <option value="">— niemand —</option>
          {benutzer.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <FeldStatus status={zustaendig} />
      </td>
      <td className="py-1.5 pr-2">
        <span className="inline-flex items-center gap-1">
          <input
            type="radio"
            name="montage"
            aria-label="Montage"
            disabled={!darf}
            checked={d.isAssembly}
            onChange={async () => {
              const e = await montage.sende({ felder: ['isAssembly'], patch: { isAssembly: true }, wert: 'isAssembly' })
              if (e.art === 'gespeichert') onGeaendert()
            }}
          />
          <FeldStatusZeichen status={montage.status} />
        </span>
        <FeldStatus status={montage.status} zeichen={false} />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="countsAsLabor" label="zählt als Arbeit" darf={darf} />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button
            type="button"
            className="text-muted-foreground hover:text-destructive"
            title="löschen — nur ohne Arbeitsschritte und Termine"
            onClick={async () => {
              if (!(await bestaetige(`Phase „${p.name}" löschen?`, { ok: 'Löschen', gefaehrlich: true }))) return
              try {
                onFehler(null)
                await deletePhase(p.id)
                onGeaendert()
              } catch (err) {
                onFehler(apiErrorText(err, 'Das hat nicht geklappt.'))
              }
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

/** Farbwähler, der erst beim Schließen übernimmt (der native `change`, nicht jede Bewegung). */
function FarbFeld({ wert, gesperrt, onWahl }: { wert: string; gesperrt: boolean; onWahl: (hex: string) => void }) {
  const [vorschau, setVorschau] = useState(wert)
  useEffect(() => setVorschau(wert), [wert])
  return (
    <input
      type="color"
      aria-label="Farbe"
      className="h-7 w-10 cursor-pointer rounded border border-border bg-background"
      disabled={gesperrt}
      value={vorschau}
      onChange={(e) => setVorschau(e.target.value)}
      onBlur={(e) => e.target.value !== wert && onWahl(e.target.value)}
    />
  )
}

function AuftragJeKategorie({ phase: p, darf }: { phase: ProjectPhase; darf: boolean }) {
  const stelle = useZeilenStelle(...phasenStelle(p))
  const vorgabe = workOrderDefaults(p)
  const titel = useFeld(stelle, 'orderTitle', { label: 'Auftragsbezeichnung', gesperrt: !darf, lies: (x) => x.trim() || null })
  const zeilen = useFeld<string[] | null>(stelle, 'signatureLines', {
    label: 'Unterschriftzeilen',
    gesperrt: !darf,
    // Eine Zeile je Unterschrift; leer = Vorgabe (`null`).
    lies: (x) => {
      const l = x
        .split('\n')
        .map((z) => z.trim())
        .filter(Boolean)
      return l.length ? l : null
    },
    schreib: (w) => (w ?? []).join('\n'),
  })
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2">{p.name}</td>
      <td className="py-1.5 pr-2">
        <input className={inlineInput} aria-label="Auftragsbezeichnung" placeholder={vorgabe.title} {...titel.props} />
        <FeldStatus status={titel} />
      </td>
      <td className="py-1.5 pr-2">
        <textarea
          className={`${inlineInput} w-full min-w-[14rem]`}
          aria-label="Unterschriftzeilen"
          rows={Math.max(2, (zeilen.wert ?? vorgabe.signatureLines).length)}
          placeholder={vorgabe.signatureLines.join('\n')}
          {...zeilen.props}
        />
        <FeldStatus status={zeilen} />
      </td>
    </tr>
  )
}

function QualifikationenKarte({ darfAendern }: { darfAendern: boolean }) {
  const { user } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const preise = darf('projects.price.view')
  const [liste, setListe] = useState<ProjectQualification[]>([])
  const [saetze, setSaetze] = useState<ProjectRegieRate[]>([])
  const [neu, setNeu] = useState({ code: '', name: '' })
  const [fehler, setFehler] = useState<string | null>(null)
  const [satzFehler, setSatzFehler] = useState<string | null>(null)

  /** Ein Schritt an den Regiesätzen — Fehler bleiben in der Karte der Sätze. */
  async function satzSchritt(schritt: () => Promise<unknown>): Promise<boolean> {
    try {
      setSatzFehler(null)
      await schritt()
      laden()
      return true
    } catch (e) {
      setSatzFehler(abrechnungsFehlerText(e))
      return false
    }
  }

  const laden = useCallback(() => {
    void fetchQualifications().then(setListe).catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
    void fetchTenantRegieRates().then(setSaetze).catch(() => undefined)
  }, [])
  useEffect(laden, [laden])

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Qualifikationen</h3>
        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1.5 pr-2">Kürzel</th>
              <th className="py-1.5 pr-2">Name</th>
              {preise && <th className="py-1.5 pr-2">Sollsatz</th>}
              <th className="py-1.5 pr-2">Mitarbeiter-Text</th>
              <th className="py-1.5 pr-2">Reihenfolge</th>
              <th className="py-1.5 pr-2">aktiv</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {liste.map((q) => (
              <QualifikationsZeile key={q.id} q={q} preise={preise} darf={darfAendern} onGeloescht={laden} onFehler={setFehler} />
            ))}
          </tbody>
        </table>

        {darfAendern && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <input
              className={inlineInput}
              placeholder="Kürzel"
              value={neu.code}
              onChange={(e) => setNeu((n) => ({ ...n, code: e.target.value.toUpperCase() }))}
            />
            <input
              className={inlineInput}
              placeholder="Name"
              value={neu.name}
              onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))}
            />
            <button
              type="button"
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              disabled={!neu.code || !neu.name}
              onClick={async () => {
                try {
                  await createQualification(neu)
                  setNeu({ code: '', name: '' })
                  laden()
                } catch (e) {
                  setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
                }
              }}
            >
              Anlegen
            </button>
          </div>
        )}
      </div>

      <div className={formCardSm}>
        <h3 className={cardTitle}>Regiesätze (Vorgabe)</h3>
        {!preise ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Sätze sehen nur Benutzer mit dem Recht „Preise und Sätze sehen".
          </p>
        ) : (
          <>
            <p className="mt-1 text-xs text-muted-foreground">
              Eine Zeitreihe je Qualifikation — der Regievorschlag nimmt den Satz, der am Buchungstag galt.
            </p>
            {satzFehler && <p className="mt-2 text-sm text-destructive">{satzFehler}</p>}
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 pr-2">Qualifikation</th>
                  <th className="py-1.5 pr-2">Satz</th>
                  <th className="py-1.5 pr-2">gültig ab</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {/* Direkt in der Tabelle (AP-35 Nachtrag 18.9.2026) — die leere Zeile am Ende legt an. */}
                {saetze.map((s) => (
                  <RegieSatzZeile
                    key={s.id}
                    satz={s}
                    name={liste.find((q) => q.id === s.qualificationId)?.name ?? '—'}
                    bearbeitbar={darfAendern}
                    // AP-36: mit dem Stand der Zeile — überholt meldet der Server 409.
                    onSpeichern={(felder) => satzSchritt(() => updateTenantRegieRate(s.id, felder, s.updatedAt))}
                    onLoeschen={() => void satzSchritt(() => deleteTenantRegieRate(s.id))}
                  />
                ))}
                {/* Qualifikationen ohne Satz stehen als eigene Zeile da — eintippen legt an. */}
                {liste
                  .filter((q) => q.isActive && !saetze.some((s) => s.qualificationId === q.id))
                  .map((q) => (
                    <RegieSatzZeile
                      key={`ohne-${q.id}`}
                      satz={{ id: `ohne-${q.id}`, qualificationId: q.id, rate: null, validFrom: null }}
                      name={q.name}
                      bearbeitbar={darfAendern}
                      loeschbar={false}
                      onSpeichern={(felder) =>
                        felder.rate == null
                          ? Promise.resolve(false)
                          : satzSchritt(() => createTenantRegieRate({ qualificationId: q.id, rate: felder.rate, ...(felder.validFrom ? { validFrom: felder.validFrom } : {}) }))
                      }
                      onLoeschen={() => undefined}
                    />
                  ))}
                {darfAendern && (
                  <NeueRegieSatzZeile
                    qualifikationen={liste}
                    onAnlegen={(felder) => satzSchritt(() => createTenantRegieRate(felder))}
                  />
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  )
}

function QualifikationsZeile({
  q,
  preise,
  darf,
  onGeloescht,
  onFehler,
}: {
  q: ProjectQualification
  preise: boolean
  darf: boolean
  onGeloescht: () => void
  onFehler: (text: string | null) => void
}) {
  const stelle = useZeilenStelle('projectQualification', `/projects/settings/qualifications/${q.id}`, q, q.code)
  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2 font-mono text-xs">{q.code}</td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="name" label="Name" darf={darf} pflicht />
      </td>
      {preise && (
        <td className="py-1.5 pr-2">
          <ZellenFeld stelle={stelle} feld="plannedCostRate" label="Sollsatz" darf={darf} zahl />
        </td>
      )}
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="matchesUserQualification" label="Mitarbeiter-Text" darf={darf} />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenFeld stelle={stelle} feld="sortOrder" label="Reihenfolge" darf={darf} klasse="w-16 text-right" pflicht zahl />
      </td>
      <td className="py-1.5 pr-2">
        <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
      </td>
      <td className="py-1.5 text-right">
        {darf && (
          <button
            type="button"
            className="text-muted-foreground hover:text-destructive"
            title="löschen — nur, wenn nichts daran hängt; sonst abschalten"
            onClick={async () => {
              if (!(await bestaetige(`Qualifikation ${q.code} löschen?`, { ok: 'Löschen', gefaehrlich: true }))) return
              try {
                onFehler(null)
                await deleteQualification(q.id)
                onGeloescht()
              } catch (err) {
                onFehler(apiErrorText(err, 'Das hat nicht geklappt.'))
              }
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

function JournalKarte({ stelle, darfAendern }: { stelle: Speicherstelle<unknown>; darfAendern: boolean }) {
  const [typen, setTypen] = useState<JournalType[]>([])
  const [neu, setNeu] = useState({ name: '', color: 'YELLOW' })
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(() => {
    void fetchJournalTypes().then(setTypen).catch(() => undefined)
  }, [])
  useEffect(laden, [laden])

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Journal-Typen</h3>
        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
        <ul className="mt-3 space-y-1 text-sm">
          {typen.map((t) => (
            <JournalTypZeile key={t.id} typ={t} darf={darfAendern} />
          ))}
        </ul>
        {darfAendern && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <input className={`${inlineInput} w-40`} placeholder="Name" value={neu.name} onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))} />
            <select className={`${inlineInput} w-28`} value={neu.color} onChange={(e) => setNeu((n) => ({ ...n, color: e.target.value }))}>
              {PROJECT_NOTE_COLORS.map((c) => (
                <option key={c} value={c}>
                  {NOTE_COLOR_LABELS[c] ?? c}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              disabled={!neu.name.trim()}
              onClick={async () => {
                try {
                  await createJournalType({ key: schluesselAus(neu.name), name: neu.name.trim(), color: neu.color })
                  setNeu({ name: '', color: 'YELLOW' })
                  laden()
                } catch (e) {
                  setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
                }
              }}
            >
              Typ anlegen
            </button>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Der System-Typ trägt die Zeilen, die das System schreibt — umbenennen ja, abschalten nie.
        </p>
      </div>

      <FelderKarte
        stelle={stelle}
        darfAendern={darfAendern}
        titel="Erinnerungen"
        felder={[
          { feld: 'taskRemindOffsetHours', label: 'Vorlauf der Erinnerung (Stunden)', art: 'zahl' },
          { feld: 'remindDailyDigest', label: 'Tages-Zusammenfassung statt Einzelmails', art: 'schalter' },
          { feld: 'digestTime', label: 'Uhrzeit der Zusammenfassung', art: 'text' },
        ]}
      />
    </div>
  )
}

function JournalTypZeile({ typ: t, darf }: { typ: JournalType; darf: boolean }) {
  const stelle = useZeilenStelle('journalType', `/projects/settings/journal-types/${t.id}`, t, t.name)
  const farbe = useFeld(stelle, 'color', { art: 'auswahl', label: 'Farbe', pflicht: true, gesperrt: !darf })
  return (
    <li className="flex flex-wrap items-center gap-2">
      <span className="w-48">
        <ZellenFeld stelle={stelle} feld="name" label="Name" darf={darf} pflicht />
      </span>
      <select className={`${inlineInput} w-28`} aria-label="Farbe" {...farbe.props}>
        {PROJECT_NOTE_COLORS.map((c) => (
          <option key={c} value={c}>
            {NOTE_COLOR_LABELS[c] ?? c}
          </option>
        ))}
      </select>
      <FeldStatusZeichen status={farbe} />
      {t.isSystem ? (
        <span className="text-xs text-muted-foreground">System</span>
      ) : (
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <ZellenHaken stelle={stelle} feld="isActive" label="aktiv" darf={darf} />
          aktiv
        </label>
      )}
    </li>
  )
}

/** Der Schlüssel eines Journal-Typs entsteht aus dem Namen — er ist technisch, nicht sichtbar. */
function schluesselAus(name: string): string {
  const key = name
    .trim()
    .toUpperCase()
    .replace(/Ä/g, 'AE')
    .replace(/Ö/g, 'OE')
    .replace(/Ü/g, 'UE')
    .replace(/ß/g, 'SS')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 30)
  return key || `TYP_${Date.now()}`
}
